import { rotulo } from './formato';
import type { Tom } from './ui';

/**
 * As escadas de situação, uma por entidade, num arquivo só (P-UI-41).
 *
 * «Uma escada de situação por entidade» — decisão D1 · A do quadro «Padrões
 * do LabManager» (Humberto, 09/10/2026; doc 303, onda 320). Cada situação tem
 * símbolo, palavra e tom, como o selo do ensaio (`SITUACAO_DO_ENSAIO`, em
 * `ui.tsx`); as chaves são as do banco; cancelado, cancelada e substituído
 * são sempre neutros. Nenhuma tela monta o próprio mapa de tom: ela lê daqui.
 *
 * Este arquivo nasce com a campanha CPTu (onda 312, Tarefa 312.5). A OS, a
 * amostra, o lote, a medição, o equipamento, o relatório, a NC e o
 * certificado vêm para cá na onda de correção (doc 303 §4).
 */
export interface DegrauDaSituacao { simbolo: string; texto: string; tom: Tom }

/** A campanha CPTu (`cptu_campanha.situacao`, 0254). */
export const SITUACAO_DA_CAMPANHA_CPTU: { readonly [situacao: string]: DegrauDaSituacao } = {
  rascunho:       { simbolo: '◐', texto: 'Rascunho',       tom: 'andamento' },
  em_verificacao: { simbolo: '?', texto: 'Em verificação', tom: 'atencao' },
  devolvida:      { simbolo: '!', texto: 'Devolvida',      tom: 'critico' },
  aprovada:       { simbolo: '✓', texto: 'Aprovada',       tom: 'ok' },
  emitida:        { simbolo: '✓', texto: 'Emitida',        tom: 'ok' },
  cancelada:      { simbolo: '×', texto: 'Cancelada',      tom: 'neutro' },
};

/** A vertical CPTu (`cptu_vertical.situacao`, 0254): a ativa e a que a reimportação substituiu (R8). */
export const SITUACAO_DA_VERTICAL_CPTU: { readonly [situacao: string]: DegrauDaSituacao } = {
  ativa:       { simbolo: '✓', texto: 'Ativa',       tom: 'ok' },
  substituida: { simbolo: '×', texto: 'Substituída', tom: 'neutro' },
  /* A campanha foi cancelada e a vertical ficou como registro; o ponto, livre (rodada final da 312). */
  cancelada:   { simbolo: '×', texto: 'Cancelada',   tom: 'neutro' },
};

/**
 * O arquivo na conferência da importação CPTu (Tarefa 312.5): lido sem nada
 * a notar, lido com aviso de atenção (nenhum bloqueia), ou recusado com motivo.
 */
export const RESULTADO_DA_CONFERENCIA_CPTU: { readonly [resultado: string]: DegrauDaSituacao } = {
  ok:         { simbolo: '✓', texto: 'OK',         tom: 'ok' },
  com_avisos: { simbolo: '▲', texto: 'Com avisos', tom: 'atencao' },
  recusado:   { simbolo: '!', texto: 'Recusado',   tom: 'critico' },
};

/**
 * O cone na data do ensaio (`fn_equipamento_impedimento_em`): apto, impedido
 * (calibração vencida, bloqueio…), ou um número que o parque não conhece.
 */
export const CONE_NA_DATA: { readonly [estado: string]: DegrauDaSituacao } = {
  apto:           { simbolo: '✓', texto: 'Apto na data',         tom: 'ok' },
  impedido:       { simbolo: '▲', texto: 'Impedido na data',     tom: 'atencao' },
  fora_do_parque: { simbolo: '?', texto: 'Fora de Equipamentos', tom: 'atencao' },
};

/*
 * ── O Comercial (onda 550, Tarefa 14) ────────────────────────────────
 *
 * Na caixa de entrada, a situação que importa é de quem é a vez: a do
 * atendimento (`SITUACAO_DO_ATENDIMENTO`), tirada da conversa — a situação
 * dela (`conversa.situacao`), quem está com ela e quem escreveu por último.
 * A entrega de cada mensagem enviada (`fila_saida.situacao`, 0504) tem a
 * escada dela; `pendente` e `enviando` são a mesma coisa para quem lê: a
 * mensagem ainda não saiu («Na fila»).
 */

/** De quem é a vez na conversa — a linha da caixa e o cabeçalho da conversa. */
export const SITUACAO_DO_ATENDIMENTO: { readonly [situacao: string]: DegrauDaSituacao } = {
  sem_resposta:       { simbolo: '!', texto: 'Sem resposta',       tom: 'atencao' },
  aberta:             { simbolo: '◐', texto: 'Aberta',              tom: 'andamento' },
  com_agente:         { simbolo: '◐', texto: 'Com o agente',        tom: 'andamento' },
  aguardando_cliente: { simbolo: '○', texto: 'Aguardando cliente',  tom: 'neutro' },
  encerrada:          { simbolo: '×', texto: 'Encerrada',           tom: 'neutro' },
};

/** A entrega de uma mensagem enviada (`fila_saida.situacao`, 0504). */
export const ENTREGA_DA_MENSAGEM: { readonly [situacao: string]: DegrauDaSituacao } = {
  pendente: { simbolo: '○', texto: 'Na fila',      tom: 'neutro' },
  enviando: { simbolo: '○', texto: 'Na fila',      tom: 'neutro' },
  enviada:  { simbolo: '✓', texto: 'Enviada',      tom: 'andamento' },
  entregue: { simbolo: '✓', texto: 'Entregue',     tom: 'ok' },
  lida:     { simbolo: '✓', texto: 'Lida',         tom: 'ok' },
  falhou:   { simbolo: '!', texto: 'Não entregue', tom: 'critico' },
};

/** O estágio da oportunidade no funil (`oportunidade.estagio`, 0501, §8.3). */
export const ESTAGIO_DA_OPORTUNIDADE: { readonly [estagio: string]: DegrauDaSituacao } = {
  novo:        { simbolo: '○', texto: 'Novo',        tom: 'neutro' },
  triagem:     { simbolo: '◐', texto: 'Triagem',     tom: 'andamento' },
  em_proposta: { simbolo: '◐', texto: 'Em proposta', tom: 'andamento' },
  enviada:     { simbolo: '◐', texto: 'Enviada',     tom: 'andamento' },
  negociacao:  { simbolo: '◐', texto: 'Negociação',  tom: 'atencao' },
  ganha:       { simbolo: '✓', texto: 'Ganha',       tom: 'ok' },
  perdida:     { simbolo: '×', texto: 'Perdida',     tom: 'neutro' },
  descartada:  { simbolo: '×', texto: 'Descartada',  tom: 'neutro' },
};

/**
 * A tarefa do Comercial (`tarefa.situacao`, 0501; «vencida» é a aberta com o
 * prazo passado, que a API diz).
 */
export const SITUACAO_DA_TAREFA: { readonly [situacao: string]: DegrauDaSituacao } = {
  aberta:    { simbolo: '○', texto: 'Aberta',    tom: 'andamento' },
  vencida:   { simbolo: '!', texto: 'Vencida',   tom: 'critico' },
  feita:     { simbolo: '✓', texto: 'Feita',     tom: 'ok' },
  cancelada: { simbolo: '×', texto: 'Cancelada', tom: 'neutro' },
};

/**
 * O nível de um item do Radar do Comercial (`fn_radar`, 0510; spec §8.6):
 * crítico, em risco ou em andamento. O mesmo degrau no Radar e no destaque
 * do cartão do funil.
 */
export const NIVEL_DO_RADAR: { readonly [nivel: string]: DegrauDaSituacao } = {
  critico:      { simbolo: '!', texto: 'Crítico',      tom: 'critico' },
  em_risco:     { simbolo: '▲', texto: 'Em risco',     tom: 'atencao' },
  em_andamento: { simbolo: '◐', texto: 'Em andamento', tom: 'andamento' },
};

/**
 * O canal do atendimento (`canal.situacao`, 0500; RN-477): desligado, em
 * teste (a mensageria já o alcança) ou no ar. Tela dos Ajustes (Tarefa 16).
 */
export const SITUACAO_DO_CANAL: { readonly [situacao: string]: DegrauDaSituacao } = {
  desligado: { simbolo: '○', texto: 'Desligado', tom: 'neutro' },
  teste:     { simbolo: '◐', texto: 'Em teste',  tom: 'andamento' },
  no_ar:     { simbolo: '✓', texto: 'No ar',     tom: 'ok' },
};

/** A conexão do canal (`canal.situacao_conexao`, 0500), que a mensageria conta. */
export const CONEXAO_DO_CANAL: { readonly [situacao: string]: DegrauDaSituacao } = {
  desconectado:  { simbolo: '○', texto: 'Desconectado',      tom: 'neutro' },
  aguardando_qr: { simbolo: '!', texto: 'Aguardando o QR',   tom: 'atencao' },
  conectado:     { simbolo: '✓', texto: 'Conectado',         tom: 'ok' },
  falha:         { simbolo: '!', texto: 'Caiu',              tom: 'critico' },
};

/**
 * O fornecedor de calibração homologado (`vw_fornecedor_calibracao.estado`,
 * 0114/0115). Uma escada só para a tela de Fornecedores e o selo do
 * certificado na ficha do equipamento (onda 351).
 */
export const SITUACAO_DO_FORNECEDOR: { readonly [estado: string]: DegrauDaSituacao } = {
  aprovado:          { simbolo: '✓', texto: 'Aprovado',          tom: 'ok' },
  aprovacao_vencida: { simbolo: '!', texto: 'Aprovação vencida', tom: 'critico' },
  em_avaliacao:      { simbolo: '?', texto: 'Em avaliação',      tom: 'atencao' },
  suspenso:          { simbolo: '!', texto: 'Suspenso',          tom: 'critico' },
  reprovado:         { simbolo: '×', texto: 'Reprovado',         tom: 'critico' },
};

/**
 * A ação final da decisão do agente (`decisao_agente.acao_final`, 0511) e a
 * decisão ainda aberta (`aberta`, sem ação final) — a aba Decisões da tela do
 * Agente (onda 552, Tarefa 24).
 */
export const ACAO_DO_AGENTE: { readonly [acao: string]: DegrauDaSituacao } = {
  aberta:      { simbolo: '○', texto: 'Em andamento',          tom: 'neutro' },
  sugerida:    { simbolo: '◐', texto: 'Sugerida',              tom: 'andamento' },
  enviada:     { simbolo: '✓', texto: 'Enviada pelo agente',   tom: 'ok' },
  instrucao:   { simbolo: '✓', texto: 'Instrução confirmada',  tom: 'ok' },
  retida:      { simbolo: '!', texto: 'Retida',                tom: 'atencao' },
  transferida: { simbolo: '→', texto: 'Passada a uma pessoa',  tom: 'neutro' },
  nenhuma:     { simbolo: '—', texto: 'Sem resposta',          tom: 'neutro' },
};

/** O agente agora (a aba Situação): ligado e na fase, no copiloto pelo teto, parado pelo corte, desligado. */
export const SITUACAO_DO_AGENTE: { readonly [situacao: string]: DegrauDaSituacao } = {
  ligado:   { simbolo: '●', texto: 'Ligado',                  tom: 'ok' },
  no_teto:  { simbolo: '!', texto: 'No teto: só sugere',      tom: 'atencao' },
  no_corte: { simbolo: '!', texto: 'Parado pelo corte do mês', tom: 'critico' },
  desligado: { simbolo: '○', texto: 'Desligado',              tom: 'neutro' },
};

/** O degrau de uma situação, com a palavra do dicionário quando a escada não a conhece. */
export function degrauDe(
  escada: { readonly [situacao: string]: DegrauDaSituacao }, situacao: string | null | undefined,
): DegrauDaSituacao {
  return escada[situacao ?? ''] ?? {
    simbolo: '•', texto: situacao ? rotulo(situacao) : '—', tom: 'neutro',
  };
}
